import { BRAND } from '@verlo/shared/lib/brand';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { peek as peekResume, take as takeResume } from '../lib/lockResume';

// Persistent device id so the backend can lock down a kiosk on repeated abuse.
const getDeviceId = () => {
  let id = localStorage.getItem('verlo_kiosk_device_id');
  if (!id) {
    id = `kiosk-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    localStorage.setItem('verlo_kiosk_device_id', id);
  }
  return id;
};

// Used until GET /auth/pin/config answers.
const FALLBACK_PIN_LENGTH = 4;
// Verlo blue — matches /verlo-logo-blue.svg and bg-verlo used across the app.
const NAVY = BRAND.verlo;

// Who can let someone on who isn't on the rota: the server says, face by face (`can_let_on`,
// its own test). These roles are only for a server that doesn't say.
const MANAGER_ROLES = new Set(['shift_lead', 'manager', 'general_manager', 'area_manager', 'operations', 'owner', 'admin']);
const isManager = (u) => MANAGER_ROLES.has(String(u.rbac_role || u.role || '').toLowerCase());

const greetingFor = (d) => {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const mmss = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// The 423 detail carries an ISO timestamp ("… locked until 2026-…+00:00"); 429 doesn't.
const retrySecondsFromDetail = (detail) => {
  const m = /until (\S+)/.exec(detail || '');
  if (m) {
    const ts = Date.parse(m[1]);
    if (!Number.isNaN(ts)) return Math.max(1, Math.round((ts - Date.now()) / 1000));
  }
  return null;
};

const KioskLoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pinLength, setPinLength] = useState(FALLBACK_PIN_LENGTH);
  // How this venue signs in (back office › Settings › Signing in): a PIN alone, or tap your
  // name and then your PIN, and maybe only people on today's rota.
  const [signIn, setSignIn] = useState({ by_name: false, rota: false });
  const [people, setPeople] = useState([]);
  const [who, setWho] = useState(null);                  // the person tapped, by name
  const whoRef = useRef(null);
  useEffect(() => { whoRef.current = who; }, [who]);
  // Not on the rota: a manager lets them on. Holds their own PIN while the manager is asked.
  const [letOn, setLetOn] = useState(null);              // { pin, message } while asking
  const [mgrId, setMgrId] = useState('');
  const [mgrPin, setMgrPin] = useState('');
  const [mgrError, setMgrError] = useState(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const [lockUntil, setLockUntil] = useState(null); // epoch ms, or null
  const [now, setNow] = useState(() => Date.now());

  // First-use PIN change (item 5). `login` → tap the manager-issued PIN; `choose` → pick a
  // private one; `confirm` → key it again. The temp PIN, the chosen PIN and the login
  // response are held in refs across the steps and never touch storage until it succeeds.
  const [phase, setPhase] = useState('login');
  const tempPinRef = useRef('');
  const chosenPinRef = useRef('');
  const pendingLoginRef = useRef(null);
  // The restricted pin_change_only token `pin_login` hands back for this flow. Kept in a ref,
  // never localStorage: it's good for nothing but `POST /auth/pin/first-set` (the backend
  // refuses it everywhere else), and keeping it out of storage means a reload lands on a clean
  // login screen rather than something that looks like a resumable session.
  const pinChangeTokenRef = useRef(null);
  const phaseRef = useRef('login');
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Refs so the window keydown listener and the async submit read live values
  // without being re-bound on every keystroke.
  const pinRef = useRef('');
  const submittingRef = useRef(false);
  const lockedRef = useRef(false);
  useEffect(() => { pinRef.current = pin; }, [pin]);
  useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  const locked = lockUntil != null && now < lockUntil;
  useEffect(() => { lockedRef.current = locked; }, [locked]);
  const lockRemaining = locked ? Math.ceil((lockUntil - now) / 1000) : 0;

  // Signed in: back where they were when the till locked, if it was them; else their landing.
  useEffect(() => {
    if (user) navigate(takeResume(user) || user.default_landing_path || '/tables', { replace: true });
  }, [user, navigate]);
  // Who was where when the till locked, for the card above the pad. Read once on arrival.
  const [resume] = useState(() => peekResume());

  // Configured PIN length (org-uniform), and how this venue signs in. Falls back silently.
  // Read on arrival, again every minute while the pad waits, and at once when the server says
  // a name comes first: a till left on the pad when the office switched names on otherwise
  // kept a pad that refused every PIN and showed no names to tap.
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  const loadSignIn = useCallback(() => {
    authApi.pinConfig()
      .then((r) => {
        if (!aliveRef.current) return;
        if (r.data?.pin_length) setPinLength(r.data.pin_length);
        if (r.data?.sign_in) setSignIn(r.data.sign_in);
        if (r.data?.sign_in?.by_name) {
          authApi.pinStaffGrid().then((g) => { if (aliveRef.current) setPeople(g.data?.users || []); }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadSignIn(); }, [loadSignIn]);
  useEffect(() => {
    if (phase !== 'login' || who) return undefined;
    const t = setInterval(loadSignIn, 60000);
    return () => clearInterval(t);
  }, [phase, who, loadSignIn]);

  // One tick a second drives both the clock and the lockout countdown.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Re-open the pad the moment a lockout elapses.
  useEffect(() => {
    if (lockUntil != null && now >= lockUntil) {
      setLockUntil(null);
      setError(null);
    }
  }, [now, lockUntil]);

  // Privacy: drop a half-entered PIN after 30s idle, or as soon as the screen is
  // backgrounded / loses focus — the next person starts clean.
  useEffect(() => {
    if (!pin) return;
    const t = setTimeout(() => { setPin(''); setError(null); }, 30000);
    return () => clearTimeout(t);
  }, [pin]);
  useEffect(() => {
    const wipe = () => { setPin(''); setError(null); };
    const onVis = () => { if (document.hidden) wipe(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', wipe);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', wipe);
    };
  }, []);

  const byName = signIn.by_name;
  const byNameRef = useRef(false);
  useEffect(() => { byNameRef.current = byName; }, [byName]);
  const tap = (d) => {
    if (submittingRef.current || lockedRef.current) return;
    if (byNameRef.current && !whoRef.current && phaseRef.current === 'login') return;   // a name first
    setError(null);
    setPin((p) => (p.length >= pinLength ? p : p + d));
  };
  const backspace = () => {
    if (submittingRef.current || lockedRef.current) return;
    setError(null);
    setPin((p) => p.slice(0, -1));
  };
  const clearPin = () => {
    if (submittingRef.current || lockedRef.current) return;
    setError(null);
    setPin('');
  };

  // Land the signed-in staffer: enrich from /me, stash, hard-navigate. `submitting` is left
  // true so the spinner holds until the page unloads.
  const completeLogin = async ({ access_token, default_landing_path, user: u }) => {
    localStorage.setItem('verlo_token', access_token);
    let who = u;
    try {
      const me = await authApi.getMe();
      who = me.data || u;
      localStorage.setItem('verlo_user', JSON.stringify(me.data));
    } catch {
      localStorage.setItem('verlo_user', JSON.stringify(u));
    }
    toast.success(`Welcome back, ${u.name || u.email}`);
    // Back where they were when the till locked, if it was them (lib/lockResume.js).
    window.location.assign(takeResume(who) || takeResume(u) || default_landing_path || '/tables');
  };

  // Release the submit guard. `setSubmitting(false)` alone is not enough: on a step that
  // toggles `submitting` true→false within one tick React never sees it change, so the
  // effect that mirrors it into `submittingRef` never runs and the keypad stays frozen.
  const releaseSubmit = () => { submittingRef.current = false; setSubmitting(false); };

  const failStep = (message, { relock } = {}) => {
    setError(message);
    if (relock) { setPhase('choose'); chosenPinRef.current = ''; }
    setShake(true);
    setTimeout(() => setShake(false), 500);
    setPin('');
    releaseSubmit();
  };

  // Bail out of the first-use PIN change entirely — a manual Cancel, or an idle timeout below.
  // Drops the restricted token along with the rest of the flow's state, so nothing about the
  // abandoned attempt is left for the next person at the kiosk to pick up.
  const resetFirstSetFlow = () => {
    tempPinRef.current = '';
    chosenPinRef.current = '';
    pendingLoginRef.current = null;
    pinChangeTokenRef.current = null;
    setPhase('login');
    setPin('');
    setError(null);
    releaseSubmit();
  };

  // The forced-change flow is a privileged, unattended window — a manager-issued PIN sitting
  // one confirm-step away from becoming a real login. Left mid-flow (a distracted new hire, an
  // interrupted handoff), the next person to walk up must not be able to continue it. Any
  // stretch of inactivity resets the whole flow, not just the typed digits — `pin` in the deps
  // restarts the clock on every keystroke.
  useEffect(() => {
    if (phase === 'login') return;
    const t = setTimeout(resetFirstSetFlow, 45000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pin]);

  const submit = async () => {
    const value = pinRef.current;
    if (value.length < pinLength || submittingRef.current) return;
    submittingRef.current = true; // sync guard — the state effect lands a tick later
    setSubmitting(true);
    setError(null);

    // --- First-use PIN change steps -----------------------------------------
    if (phaseRef.current === 'choose') {
      chosenPinRef.current = value;
      setPhase('confirm');
      setPin('');
      releaseSubmit();
      return;
    }
    if (phaseRef.current === 'confirm') {
      if (value !== chosenPinRef.current) {
        failStep("That didn't match. Choose your PIN again.", { relock: true });
        return;
      }
      try {
        await authApi.pinFirstSet(
          { current_pin: tempPinRef.current, new_pin: value },
          pinChangeTokenRef.current,
        );
        pinChangeTokenRef.current = null; // single-purpose — spent the moment first-set succeeds
      } catch (e) {
        failStep(e.response?.data?.detail || "Couldn't set that PIN. Try another.", { relock: true });
        return;
      }
      // The token that let them choose is good for nothing else, so sign in afresh with the
      // PIN they chose. The till fetches it as it's set; if it hasn't arrived, they enter it.
      try {
        const res = await authApi.pinLogin({ pin: value, ...(whoRef.current ? { user_id: whoRef.current.id } : {}) }, getDeviceId());
        if (!res.data.pin_must_change) {
          await completeLogin(res.data);
          return;
        }
      } catch {
        // Not here yet: ask for it below.
      }
      resetFirstSetFlow();
      toast.success('Your PIN is set. Enter it to start.');
      return;
    }

    // --- Ordinary PIN login -----------------------------------------------
    try {
      // Keypad-only, the PIN identifies the staffer; signing in by name, it's that person's.
      const res = await authApi.pinLogin({ pin: value, ...(whoRef.current ? { user_id: whoRef.current.id } : {}) }, getDeviceId());
      if (res.data.pin_must_change) {
        // Manager-issued PIN: don't let them in until they've chosen their own. Keep the
        // restricted token in memory only — never `verlo_token` — so nothing that looks like
        // a signed-in session survives a reload; `pinFirstSet` sends it explicitly instead.
        tempPinRef.current = value;
        pendingLoginRef.current = res.data;
        pinChangeTokenRef.current = res.data.access_token;
        setPhase('choose');
        setPin('');
        releaseSubmit();
        return;
      }
      await completeLogin(res.data);
    } catch (e) {
      const code = e.response?.status;
      const detail = e.response?.data?.detail;
      if (code === 423 || code === 429) {
        const secs = retrySecondsFromDetail(detail) ?? (code === 429 ? 60 : 300);
        setLockUntil(Date.now() + secs * 1000);
        setError(code === 429 ? 'Too many attempts on this till' : 'Too many wrong tries');
      } else if (code === 403 && detail?.code === 'not_on_rota') {
        // Their PIN was right; today's rota doesn't have them. A manager can let them on.
        setLetOn({ pin: value, message: detail.message });
        setMgrId(''); setMgrPin(''); setMgrError(null);
        setPin('');
        releaseSubmit();
        return;
      } else if (code === 400 && detail?.code === 'name_first') {
        // The office switched to names after this till last read how to sign in. Read it
        // again, and the names come up.
        setError(detail.message);
        loadSignIn();
      } else if (code === 409) {
        setError('More than one person uses that PIN. Ask a manager to reset it.');
      } else {
        // No count of tries left (Oct 2026): a PIN that matches nobody locks nobody, so the
        // number meant nothing, and it told a guesser how far to go. The lockout says itself.
        setError(typeof detail === 'string' && !/^invalid/i.test(detail) ? detail : "That PIN didn't match");
      }
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
      setSubmitting(false);
    }
  };

  // A manager lets someone on who isn't on today's rota: their PIN, then the person's again.
  const letThemOn = async () => {
    if (!mgrId) { setMgrError('Choose the manager letting them on'); return; }
    if (!/^\d{4,6}$/.test(mgrPin)) { setMgrError('Enter the manager’s PIN'); return; }
    setSubmitting(true); submittingRef.current = true;
    try {
      const res = await authApi.pinLogin({ pin: letOn.pin, user_id: who.id, manager_id: mgrId, manager_pin: mgrPin }, getDeviceId());
      const theirPin = letOn.pin;
      setLetOn(null);
      if (res.data.pin_must_change) {
        // A new starter let on: they choose their own PIN first, as at the pad. The token is
        // only for that, so it stays in memory and never becomes the session.
        tempPinRef.current = theirPin;
        pendingLoginRef.current = res.data;
        pinChangeTokenRef.current = res.data.access_token;
        setPhase('choose');
        setPin('');
        releaseSubmit();
        return;
      }
      await completeLogin(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setMgrError(typeof d === 'string' ? d : "That manager's PIN isn't right");
      setMgrPin('');
      releaseSubmit();
    }
  };
  // A name tapped and left: back to the names after 30 seconds, for the next person. Left on
  // the manager's step too (a minute, restarted as the manager types): the person's PIN,
  // already checked, isn't kept for whoever comes to the till next.
  useEffect(() => {
    if (!who || pin) return undefined;
    const t = setTimeout(() => { setWho(null); setLetOn(null); setMgrPin(''); }, letOn ? 60000 : 30000);
    return () => clearTimeout(t);
  }, [who, pin, letOn, mgrPin]);
  const chooseAgain = () => { setWho(null); setLetOn(null); setPin(''); setError(null); };
  // Today's people first, when the rota decides; then everyone else, faded.
  const ordered = [...people].sort((a, b) => Number(b.on_rota === true) - Number(a.on_rota === true) || String(a.name).localeCompare(String(b.name)));
  const showNames = byName && !who && phase === 'login';

  // Auto-submit once the PIN reaches the expected length.
  useEffect(() => {
    if (pin.length >= pinLength && !submitting && !locked) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  // Physical keyboard / USB numpad: digits, Backspace, Enter, Esc.
  useEffect(() => {
    const onKey = (e) => {
      if (submittingRef.current || lockedRef.current) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return; // don't hijack shortcuts
      if (e.key >= '0' && e.key <= '9') { e.preventDefault(); tap(e.key); }
      else if (e.key === 'Backspace') { e.preventDefault(); backspace(); }
      else if (e.key === 'Escape') { e.preventDefault(); clearPin(); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        if (pinRef.current.length >= pinLength) submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinLength]);

  const clock = new Date(now);
  const timeStr = clock.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const dateStr = clock.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  const srStatus = submitting
    ? (phase === 'confirm' ? 'Saving your new PIN' : 'Signing in')
    : locked
      ? `Locked. Try again in ${mmss(lockRemaining)}`
      : error
        ? error
        : phase === 'choose'
          ? `Choose a new PIN: ${pin.length} of ${pinLength} digits entered`
          : phase === 'confirm'
            ? `Confirm your new PIN: ${pin.length} of ${pinLength} digits entered`
            : `${pin.length} of ${pinLength} digits entered`;

  // Keys wear the table-card look from /tables: white, hairline border, soft shadow.
  const keyBase =
    'aspect-square rounded-xl bg-white border border-hairline shadow-sm hover:shadow-md ' +
    'transition-all active:scale-95 active:bg-surface-2 disabled:opacity-50 disabled:pointer-events-none ' +
    'flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'focus-visible:ring-verlo';

  return (
    <div
      className="kiosk-screen min-h-screen bg-surface-2 text-ink flex flex-col items-center
                 px-6 py-6 gap-8 overflow-y-auto justify-center portrait:justify-between portrait:py-[12vh]
                 [@media(max-height:820px)]:gap-3 [@media(max-height:820px)]:py-3"
      onContextMenu={(e) => e.preventDefault()}
      data-testid="kiosk-login"
    >
      <p role="status" aria-live="polite" className="sr-only">{srStatus}</p>

      {/* Greeting + clock, then the logo — same width as the pad below. */}
      <div className="w-full max-w-xs shrink-0">
        <p className="text-xs text-ink-faint text-center mb-6 tracking-wide [@media(max-height:820px)]:mb-2">
          {greetingFor(clock)} · {timeStr} · {dateStr}
        </p>
        <img
          src="/verlo-logo-blue.svg"
          alt="Verlo"
          className="w-full h-auto max-h-20 object-contain [@media(max-height:820px)]:max-h-11"
          data-testid="kiosk-logo"
        />
      </div>

      {/* A till screen never scrolls (Chris's review H1): on a short one (1024×768 with the
          resume banner) the logo, the gaps and the pad all tighten so the 0 stays in reach. */}
      <div className="w-full max-w-xs [@media(max-height:820px)]:max-w-[17rem]">
        {resume && phase === 'login' && (
          <div className="mb-5 flex items-center gap-3 border border-hairline border-l-[3px] border-l-signal-warn-line bg-white px-3.5 py-2.5 [@media(max-height:820px)]:mb-3 [@media(max-height:820px)]:py-1.5" data-testid="lock-resume">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-verlo text-[11px] font-bold text-white">
              {(resume.name || '?').slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0 text-left">
              <div className="text-sm font-semibold text-ink">{resume.name} was on {resume.label}</div>
              <div className="text-xs text-ink-soft">
                {resume.unsent > 0 ? `${resume.unsent} item${resume.unsent === 1 ? '' : 's'} not sent yet · ` : ''}
                {resume.name}’s PIN goes straight back there
              </div>
            </div>
          </div>
        )}
        {/* First-use PIN change prompt */}
        {phase !== 'login' && (
          <div className="text-center mb-4">
            <p className="text-sm text-ink-soft" data-testid="kiosk-firstset-prompt">
              {phase === 'choose'
                ? 'Choose your own PIN. The one you were given is temporary.'
                : 'Enter your new PIN again to confirm'}
            </p>
            <button
              type="button"
              onClick={resetFirstSetFlow}
              disabled={submitting}
              className="mt-2 text-xs uppercase tracking-wider font-semibold text-ink-faint
                         hover:text-ink-soft disabled:opacity-50"
              data-testid="kiosk-firstset-cancel"
            >
              Cancel
            </button>
          </div>
        )}

        {showNames && (
          <div className="space-y-3" data-testid="kiosk-names">
            <p className="text-center text-sm font-medium text-ink-soft">
              Who's this?{signIn.rota ? <span className="text-ink-faint"> · on the rota today first</span> : null}
            </p>
            <div className="grid max-h-[52vh] grid-cols-3 gap-2 overflow-y-auto">
              {ordered.map((u) => {
                const off = signIn.rota && u.on_rota === false;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { setWho(u); setError(null); }}
                    className={`flex flex-col items-center gap-1.5 border border-hairline bg-white px-1.5 py-3 shadow-sm hover:shadow-md ${off ? 'opacity-50' : ''}`}
                    data-testid={`kiosk-person-${u.id}`}
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-verlo text-[12px] font-bold text-white">{u.initials}</span>
                    <span className="max-w-full truncate text-[13px] font-semibold text-ink">{String(u.name || '').split(' ')[0]}</span>
                    {off && <span className="text-[10.5px] text-ink-faint">not on today</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {byName && who && phase === 'login' && (
          <div className="mb-4 flex items-center justify-center gap-2" data-testid="kiosk-who">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-verlo text-[11px] font-bold text-white">{who.initials}</span>
            <span className="text-sm font-semibold text-ink">{who.name}</span>
            <button type="button" onClick={chooseAgain} className="ml-2 text-xs font-semibold uppercase tracking-wider text-ink-faint hover:text-ink-soft" data-testid="kiosk-not-me">
              Not you?
            </button>
          </div>
        )}

        {letOn && (
          <div className="mb-4 space-y-3 border border-hairline border-l-[3px] border-l-signal-warn-line bg-white p-3.5 text-left" data-testid="kiosk-let-on">
            <p className="text-sm font-semibold text-ink">{letOn.message}</p>
            <select
              value={mgrId}
              onChange={(e) => { setMgrId(e.target.value); setMgrError(null); }}
              className="h-11 w-full border border-hairline bg-white px-2 text-sm"
              aria-label="Manager"
              data-testid="kiosk-let-on-manager"
            >
              <option value="">Choose a manager</option>
              {/* Everyone the server would accept, by its own test: a shift lead, and a
                  manager who isn't on the rota letting themself on (it's audited). */}
              {people.filter((u) => u.can_let_on ?? isManager(u)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={mgrPin}
              onChange={(e) => { setMgrPin(e.target.value.replace(/\D/g, '')); setMgrError(null); }}
              placeholder="Manager's PIN"
              className="h-11 w-full border border-hairline bg-white px-3 text-center font-mono text-xl tracking-[0.4em] placeholder:font-sans placeholder:text-sm placeholder:tracking-normal"
              data-testid="kiosk-let-on-pin"
            />
            {mgrError && <p className="text-[12.5px] font-medium text-signal-stop" role="alert">{mgrError}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={chooseAgain} className="h-11 flex-1 border border-hairline bg-white text-sm font-semibold text-ink-soft">Back</button>
              <button type="button" onClick={letThemOn} disabled={submitting} className="h-11 flex-[2] bg-verlo text-sm font-semibold text-white disabled:opacity-60" data-testid="kiosk-let-on-confirm">
                Let them on
              </button>
            </div>
          </div>
        )}

        {!showNames && !letOn && (<>
        {/* PIN dots / submitting spinner */}
        <div className="mb-8 min-h-[32px] flex items-center justify-center [@media(max-height:820px)]:mb-2" aria-hidden="true">
          {submitting ? (
            <Loader2 size={28} className="animate-spin" style={{ color: NAVY }} />
          ) : (
            <div
              className={`flex justify-center gap-4 ${shake ? 'animate-kiosk-shake' : ''}`}
              data-testid="kiosk-pin-dots"
            >
              {Array.from({ length: pinLength }).map((_, i) => {
                const filled = i < pin.length;
                const paint = shake ? '#F43F5E' : filled ? NAVY : null;
                return (
                  <span
                    key={i}
                    className="w-4 h-4 rounded-full border-2 transition-colors"
                    style={{
                      backgroundColor: paint || 'transparent',
                      borderColor: paint || '#CBD5E1',
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Error / lockout countdown */}
        <div className="min-h-[36px] mb-3 text-center [@media(max-height:820px)]:mb-1" aria-hidden="true" data-testid="kiosk-pin-error">
          {locked ? (
            <p className="text-signal-stop text-sm inline-flex items-center justify-center gap-1.5">
              <AlertTriangle size={14} />
              {error || 'Locked'} · try again in {mmss(lockRemaining)}
            </p>
          ) : error ? (
            <p className="text-signal-stop text-sm inline-flex items-center justify-center gap-1.5">
              <AlertTriangle size={14} />
              {error}
            </p>
          ) : null}
        </div>

        {/* Numeric pad */}
        <div className="grid grid-cols-3 gap-3" role="group" aria-label="PIN keypad" data-testid="kiosk-pin-pad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => tap(String(d))}
              disabled={submitting || locked}
              aria-label={`Digit ${d}`}
              className={`${keyBase} text-3xl font-mono-display font-semibold`}
              style={{ color: NAVY }}
              data-testid={`kiosk-key-${d}`}
            >
              {d}
            </button>
          ))}
          <button
            type="button"
            onClick={clearPin}
            disabled={submitting || locked}
            aria-label="Clear PIN"
            className={`${keyBase} text-xs uppercase tracking-wider font-semibold text-ink-faint`}
            data-testid="kiosk-key-clear"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => tap('0')}
            disabled={submitting || locked}
            aria-label="Digit 0"
            className={`${keyBase} text-3xl font-mono-display font-semibold`}
            style={{ color: NAVY }}
            data-testid="kiosk-key-0"
          >
            0
          </button>
          <button
            type="button"
            onClick={backspace}
            disabled={submitting || locked}
            aria-label="Delete last digit"
            className={keyBase}
            data-testid="kiosk-key-back"
          >
            <Delete size={22} className="text-ink-faint" />
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default KioskLoginPage;
